'use client';

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { matterKeys, overviewKeys, taskKeys } from '@/lib/query-keys';
import { MATTER_ESTATE_ADMIN_KEYS } from '@/lib/hooks/use-matter-estate-admin-data';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { NotificationsWorkflowWithDialog } from './notifications-workflow/notifications-workflow-dialog';
import { CollectionRequestWorkflowWithDialog } from './collection-request-workflow/collection-request-workflow-dialog';
import { FollowUpWorkflowWithDialog } from './follow-up-workflow/follow-up-workflow-dialog';
import type { WorkflowType } from '@/lib/services/correspondences/workflows/workflow-types';

interface CorrespondenceWorkflowWrapperProps {
  matterId: number;
  type: WorkflowType;
  institutionId?: number;
  parentCorrespondenceId?: number;
  title?: string;
}

/**
 * Wrapper component that handles correspondence workflows
 * This component listens for workflow events from the CTA system
 */
export function CorrespondenceWorkflowWrapper() {
  const [isOpen, setIsOpen] = useState(false);
  const [workflowConfig, setWorkflowConfig] =
    useState<CorrespondenceWorkflowWrapperProps | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Listen for workflow open events
    const handleOpenWorkflow = (
      event: CustomEvent<CorrespondenceWorkflowWrapperProps>
    ) => {
      setWorkflowConfig(event.detail);
      setIsOpen(true);
    };

    window.addEventListener(
      'open-correspondence-workflow',
      handleOpenWorkflow as EventListener
    );

    return () => {
      window.removeEventListener(
        'open-correspondence-workflow',
        handleOpenWorkflow as EventListener
      );
    };
  }, []);

  const handleComplete = (data: Record<string, unknown>) => {
    console.log('Workflow completed:', data);
    const matterId = workflowConfig?.matterId;

    setIsOpen(false);
    setWorkflowConfig(null);

    // Invalidate task-related caches since completing correspondence may complete tasks
    if (matterId) {
      // Invalidate main task list
      queryClient.invalidateQueries({
        queryKey: taskKeys.forMatter(matterId)
      });

      // Invalidate task dashboard data (both aggregated and non-aggregated formats)
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as (string | number)[];
          return (
            key[0] === 'matter' &&
            key[1] === matterId &&
            key[key.length - 1] === 'tasks-dashboard-data'
          );
        }
      });

      // Invalidate institution-specific task caches
      queryClient.invalidateQueries({
        predicate: (query) => {
          const [first, second, third] = query.queryKey as string[];
          return (
            first === 'matter' &&
            second === String(matterId) &&
            third === 'institution-tasks-dashboard'
          );
        }
      });

      // Invalidate overview data (contains next steps for overview tab)
      queryClient.invalidateQueries({
        queryKey: MATTER_ESTATE_ADMIN_KEYS.overview(matterId)
      });

      // Invalidate direct-fetch overview data (used by /matters/[id]/overview page)
      queryClient.invalidateQueries({
        queryKey: overviewKeys.forMatter(matterId)
      });

      // Invalidate the main matter query to refresh task counts
      queryClient.invalidateQueries({
        queryKey: matterKeys.detail(matterId)
      });
    }

    // Dispatch completion event for other components to listen to
    window.dispatchEvent(
      new CustomEvent('correspondence-workflow-completed', {
        detail: {
          type: workflowConfig?.type,
          matterId: workflowConfig?.matterId,
          data
        }
      })
    );
  };

  const handleClose = () => {
    const matterId = workflowConfig?.matterId;

    setIsOpen(false);
    setWorkflowConfig(null);

    // Force refetch of task data when dialog closes to ensure Next Steps card updates
    if (matterId) {
      // Refetch task dashboard data (both aggregated and non-aggregated formats)
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey as (string | number)[];
          return (
            key[0] === 'matter' &&
            key[1] === matterId &&
            key[key.length - 1] === 'tasks-dashboard-data'
          );
        }
      });
      // Also refetch institution-specific task caches
      queryClient.refetchQueries({
        predicate: (query) => {
          const [first, second, third] = query.queryKey as string[];
          return (
            first === 'matter' &&
            second === String(matterId) &&
            third === 'institution-tasks-dashboard'
          );
        }
      });
      // Invalidate overview data to refresh Next Steps on overview tab
      queryClient.invalidateQueries({
        queryKey: MATTER_ESTATE_ADMIN_KEYS.overview(matterId)
      });

      // Invalidate direct-fetch overview data (used by /matters/[id]/overview page)
      queryClient.invalidateQueries({
        queryKey: overviewKeys.forMatter(matterId)
      });
    }
  };

  if (!workflowConfig) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-[95vw] overflow-y-auto">
        {/* DialogHeader and DialogTitle are now handled inside the workflow components */}
        {workflowConfig.type === 'notification' ? (
          <NotificationsWorkflowWithDialog
            matterId={workflowConfig.matterId}
            onComplete={handleComplete}
          />
        ) : workflowConfig.type === 'follow-up' ? (
          <FollowUpWorkflowWithDialog
            matterId={workflowConfig.matterId}
            institutionId={workflowConfig.institutionId!}
            parentCorrespondenceId={workflowConfig.parentCorrespondenceId!}
            onComplete={handleComplete}
            showEmailOnly={true}
          />
        ) : (
          <CollectionRequestWorkflowWithDialog
            matterId={workflowConfig.matterId}
            institutionId={workflowConfig.institutionId}
            onComplete={handleComplete}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Returns helpers to programmatically open the correspondence workflow.
 */
export function correspondenceWorkflowControls() {
  const openWorkflow = (config: CorrespondenceWorkflowWrapperProps) => {
    window.dispatchEvent(
      new CustomEvent('open-correspondence-workflow', {
        detail: config
      })
    );
  };

  return { openWorkflow };
}
